import { slices } from "@/constants/sliceNames";
import { createAsyncThunk } from "@reduxjs/toolkit";

import axios, { axiosplain } from "../../../axios/defaultAxios";
import { Execution } from "../../../entities";
import { errorMessage, successMessage } from "../../actions/messages.actions";
import * as actionTypes from "../../actions/actions";

import { addExecutionSource } from "./utils";

const ENDPOINT = "/appexecutions/v2";

/******* Get my executions ********/

export const requestMyExecutions = createAsyncThunk(
  `${slices.executions}/requestMyExecutions`,
  async (params = {}, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await axios.get(`${ENDPOINT}?${new URLSearchParams(params).toString()}`);
      if (response.status === 200 || response.status === 201) {
        const { user_id } = getState()[slices.auth].userInfo;
        // add source to executions map it to execution object
        const executions = response.data.data.data
          .map((execution) => addExecutionSource(execution, user_id))
          .map(Execution.fromApi);
        return {
          executions: executions,
          pageCount: response.data.data.records_count,
        };
      }
      throw new Error(response.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response.data.error || error.message);
    }
  }
);

export const requestMyExecutionsStatus = {
  [requestMyExecutions.fulfilled]: (state, action) => {
    state.executionsList = action.payload.executions;
    state.pageCount = action.payload.pageCount;
    state.loading = false;
  },
  [requestMyExecutions.pending]: (state, action) => {
    state.loading = true;
  },
  [requestMyExecutions.rejected]: (state, action) => {
    state.loading = false;
  },
};

/******* Update execution name ********/

export const requestUpdateExecutionName = createAsyncThunk(
  `${slices.executions}/requestUpdateExecutionName`,
  async ({ execId, execName }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.put(`${ENDPOINT}/rename/${execId}`, {
        name: execName,
      });

      if (response.status === 200 || response.status === 201) {
        dispatch(successMessage(response.data.message));
        return {
          execId,
          execName,
        };
      }
      throw new Error(response.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response.data.error || error.message);
    }
  }
);

export const requestUpdateExecutionNameStatus = {
  [requestUpdateExecutionName.fulfilled]: (state, action) => {
    const toUpdateIndex = state.executionsList.findIndex((execution) => execution.id === action.payload.execId);
    state.executionsList[toUpdateIndex].name = action.payload.execName;
    state.loading = false;
  },
  [requestUpdateExecutionName.pending]: (state, action) => {
    state.loading = true;
  },
  [requestUpdateExecutionName.rejected]: (state, action) => {
    state.loading = false;
  },
};

/******* Delete execution ********/

// TODO: Test this

export const requestDeleteExecution = createAsyncThunk(
  `${slices.executions}/requestDeleteExecution`,
  async (execId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(`${ENDPOINT}/${execId}`);

      if (response.status === 200 || response.status === 201) {
        dispatch(successMessage(response.data.message));
        return {
          execId,
        };
      }
      throw new Error(response.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response.data.error || error.message);
    }
  }
);

export const requestDeleteExecutionStatus = {
  [requestDeleteExecution.fulfilled]: (state, action) => {
    state.executionsList = state.executionsList.filter((execution) => execution.id !== action.payload.execId);
    state.loading = false;
  },
  [requestDeleteExecution.pending]: (state, action) => {
    state.loading = true;
  },
  [requestDeleteExecution.rejected]: (state, action) => {
    state.loading = false;
  },
};

/******* Rerun execution ********/

export const requestRerunExecution = createAsyncThunk(
  `${slices.executions}/requestRerunExecution`,
  async ({ execId, toProcessItem }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`${ENDPOINT}/rerun/${execId}`, {
        to_process_items: [toProcessItem],
      });

      if (response.status === 200 || response.status === 201) {
        dispatch(successMessage("Execution restarted Successfully"));
        return {
          execId,
        };
      }
      throw new Error(response.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const requestRerunExecutionStatus = {
  [requestRerunExecution.fulfilled]: (state, action) => {
    state.loading = false;
  },
  [requestRerunExecution.pending]: (state, action) => {
    state.loading = true;
  },
  [requestRerunExecution.rejected]: (state, action) => {
    state.loading = false;
  },
};

/******* Get single execution ********/

export const requestSingleExecution = createAsyncThunk(
  `${slices.executions}/requestSingleExecution`,
  async (execId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${ENDPOINT}/single/${execId}`);
      if (response.status === 200 || response.status === 201) {
        return Execution.fromApi(response.data.data);
      }
      throw new Error(response.data?.error);
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response.data.error || error.message);
    }
  }
);

export const requestSingleExecutionStatus = {
  [requestSingleExecution.fulfilled]: (state, action) => {
    state.execution = action.payload;
    state.loading = false;
  },
  [requestSingleExecution.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSingleExecution.rejected]: (state, action) => {
    state.loading = false;
  },
};

/******* Get Model Execution Details ********/

export const requestModelExecutionDetails = createAsyncThunk(
  `${slices.executions}/requestModelExecutionDetails`,
  async (execId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`/file/v2/${execId}`);
      const s3Link = response.data.data.s3_link;
      const s3Response = await axiosplain.get(s3Link);
      return s3Response.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response.data.error || error.message);
    }
  }
);

export const requestModelExecutionDetailsStatus = {
  [requestModelExecutionDetails.fulfilled]: (state, action) => {
    state.details = action.payload;
    state.detailsLoading = false;
  },
  [requestModelExecutionDetails.pending]: (state, action) => {
    state.detailsLoading = true;
  },
  [requestModelExecutionDetails.rejected]: (state, action) => {
    state.detailsLoading = false;
  },
};

/******* Get execution error file ********/

export const requestExecutionErrorFile = createAsyncThunk(
  `${slices.executions}/requestExecutionErrorFile`,
  async (execId, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${ENDPOINT}/${execId}/error_file`);
      return response.data.data;
    } catch (error) {
      dispatch(errorMessage(error.response?.data?.error || error.message));
      return rejectWithValue(error.response.data.error || error.message);
    }
  }
);

export const fetchExecutionErrorsFile = (id) => {
  const url = `/appexecutions/v2/${id}/errors_file`;
  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => res.data.data)
      .catch((err) => {
        dispatch({
          type: actionTypes.ERORR_MESSAGE,
          error: err.response?.data?.error ?? err.message,
        });
      });
  };
};
